import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Joi from "joi-browser";
import moment from "moment";
import { toast } from "react-toastify";
import { json2xml } from "xml-js";
import Input from "./Input";
import Select from "./Select";
import UpdateRecordsModal from "./UpdateRecordsModal";
import contactOptionSets from "../../options/contact.json";
import {
  getAddresses,
  getClientCompanies,
  getClientDiagnosisDetails,
  getContact,
  saveReferral,
  writeErrorLog,
} from "../services/soar";
import {
  createEntity,
  getFieldName,
  getFieldText,
  updateEntity,
} from "./../../scripts/helperScripts";

function ReferralDecisionModal({
  isOpen,
  hideModal,
  referralData,
  setReferralData,
  dynamicData,
  setDynamicData,
  referralStatus,
  programUnits,
  updateReferrals,
  programUnitOptions,
}) {
  const [dateOfAdmission, setDateOfAdmission] = useState("");
  const [isUpdateRecordsModalOpen, setIsUpdateRecordsModalOpen] =
    useState(false);
  const [isUpdateRecordsModalLoading, setIsUpdateRecordsModalLoading] =
    useState(false);
  const [clientId, setClientId] = useState("");
  const [clientProgram, setClientProgram] = useState({});
  const [fosterHomeProgram, setFosterHomeProgram] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [updateAddresses, setUpdateAddresses] = useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const [transferProgram, setTransferProgram] = useState("");
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  var org =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "ReactLive"
      : "ReactDev";
  var logOrg =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "SOARLive"
      : "SOARD365Base";

  const dateOfAdmissionSchema = {
    dateOfAdmission: Joi.date()
      .required()
      .max(moment().add(5, "days").toDate())
      .min(new Date("1/1/1900"))
      .label("Date of Admission")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date of Admission cannot be future dated`;
              break;
            case "date.min":
              error.message = `Date of Admission cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  const isDateOfAdmissionValid = (admitDate) => {
    const { error } = Joi.validate(
      { dateOfAdmission: admitDate },
      dateOfAdmissionSchema
    );

    const errs = { ...errors };

    if (error) {
      errs.DateOfAdmission = error.details[0].message;
      setErrors(errs);
      return false;
    }

    delete errs.DateOfAdmission;
    setErrors(errs);
    return true;
  };

  const createContactRecords = async (state) => {
    const contactObj = {
      ccx_contacttype: {
        type: "optionset",
        value: 803080001,
      },
      firstname: {
        type: "string",
        value: referralData.FirstName,
      },
      lastname: {
        type: "string",
        value: referralData.LastName,
      },
    };

    if (referralData.MiddleName && referralData.MiddleName !== "")
      contactObj.middlename = {
        type: "string",
        value: referralData.MiddleName,
      };
    if (referralData.DOB && referralData.DOB !== "")
      contactObj.birthdate = referralData.DOB["@date"]
        ? {
            type: "datetime",
            value: new Date(referralData.DOB["@date"]),
          }
        : {
            type: "datetime",
            value: new Date(referralData.DOB),
          };
    if (referralData.GenderValue && referralData.GenderValue !== "")
      contactObj.gendercode = {
        type: "optionset",
        value: parseInt(referralData.GenderValue),
      };
    if (referralData.SSN && referralData.SSN !== "")
      contactObj.ccx_ssn = { type: "string", value: referralData.SSN };
    if (referralData.ReferringAgency)
      contactObj.sfy_referringagency = {
        type: "string",
        value: referralData.ReferringAgency,
      };

    const toastId = toast.loading("Creating Contact...", {
      type: toast.TYPE.INFO,
    });
    const response = await createEntity(
      "contact",
      JSON.stringify(contactObj),
      safyInfo,
      state
    );

    if (!response?.resultset?.result) {
      toast.update(toastId, {
        render: "An error occurred while creating the Contact...",
      });
      return;
    }

    const contactResult = response.resultset.result;

    if (isMountedRef.current) {
      setClientId(contactResult.contactid);
    }

    const clientCompanyObj = {
      ccx_client: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: contactResult.contactid,
        },
      },
      ccx_company: {
        type: "entity",
        value: {
          entityName: "ccx_agency",
          entityId: getProgramUnitCompany().companyId,
        },
      },
      ccx_admitdate: {
        type: "datetime",
        value: new Date(dateOfAdmission),
      },
    };

    toast.update(toastId, {
      render: "Contact created. Creating Contact Company...",
    });

    const clientCompanyResponse = await createEntity(
      "ccx_clientagency",
      JSON.stringify(clientCompanyObj),
      safyInfo,
      state
    );

    if (!clientCompanyResponse?.resultset?.result) {
      toast.update(toastId, {
        render: "An error occurred while creating the Contact Company...",
      });
      return;
    }

    const clientCompanyResult = clientCompanyResponse.resultset.result;
    toast.update(toastId, {
      render: "Contact Company created. Creating Admission Records...",
    });
    createAdmissionRecords(
      contactResult.contactid,
      clientCompanyResult.ccx_clientagencyid,
      state,
      toastId
    );
  };

  const createClientCompanyRecords = async (state, proposedProgramCompany) => {
    const clientCompanyObj = {
      ccx_client: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: referralData.SOARContactId,
        },
      },
      ccx_company: {
        type: "entity",
        value: {
          entityName: "ccx_agency",
          entityId: proposedProgramCompany.companyId,
        },
      },
      ccx_admitdate: {
        type: "datetime",
        value: dateOfAdmission,
      },
    };

    const toastId = toast.loading("Creating Contact Company...", {
      type: toast.TYPE.INFO,
    });
    const clientCompanyResponse = await createEntity(
      "ccx_clientagency",
      JSON.stringify(clientCompanyObj),
      safyInfo,
      state
    );

    if (!clientCompanyResponse?.resultset?.result) {
      toast.update(toastId, {
        render: "An error occurred while creating the Contact Company...",
      });
      return;
    }

    const clientCompanyResult = clientCompanyResponse.resultset.result;
    toast.update(toastId, {
      render: "Contact Company created. Creating Admission Records...",
    });
    createAdmissionRecords(
      referralData.SOARContactId,
      clientCompanyResult.ccx_clientagencyid,
      state,
      toastId
    );
  };

  const updateContactRecord = async (state) => {
    const contactObj = {};
    const contact = await getContact(referralData.SOARContactId);

    if (!contact) {
      return;
    }

    if (getFieldText(contact.ccx_contacttype) !== "803080001") {
      contactObj.ccx_contacttype = { type: "optionset", value: 803080001 };
    }

    if (referralData.ReferringAgency) {
      contactObj.sfy_referringagency = {
        type: "entity",
        value: {
          entityName: "account",
          entityId: referralData.ReferringAgency,
        },
      };
    }

    if (Object.keys(contactObj).length > 0) {
      updateEntity(
        "contact",
        referralData.SOARContactId,
        JSON.stringify(contactObj),
        safyInfo
      );
    }

    if (isMountedRef.current) {
      setClientId(referralData.SOARContactId);
    }

    const proposedProgramCompany = getProgramUnitCompany();

    if (proposedProgramCompany) {
      const clientCompaniesResponse = await getClientCompanies(
        referralData.SOARContactId,
        safyInfo.user.safyInfo.BusinessUnitIdName
      );
      if (clientCompaniesResponse) {
        const existingCompany = clientCompaniesResponse.filter(
          (company) =>
            getFieldText(company.ccx_company) ===
            proposedProgramCompany.companyId
        )[0];

        if (!existingCompany) {
          createClientCompanyRecords(state, proposedProgramCompany);
        } else {
          const toastId = toast.loading("Creating Admission Records...", {
            type: toast.TYPE.INFO,
          });
          createAdmissionRecords(
            referralData.SOARContactId,
            existingCompany.ccx_clientagencyid,
            state,
            toastId
          );
        }
      } else {
        createClientCompanyRecords(state, proposedProgramCompany);
      }
    }
  };

  const handleAccept = async () => {
    const obj = { ...referralData };
    obj.Status = "Accepted";
    setReferralData(obj);

    hideModal();
    const selectedProgramUnit = programUnits.filter(
      (unit) => unit.ccx_unitid === `{${referralData.ProposedProgram}}`
    )[0];

    const state = getFieldName(
      selectedProgramUnit["facility.ccx_agency"]
    ).split(" of ")[1];

    setIsUpdateRecordsModalOpen(true);
    setIsUpdateRecordsModalLoading(true);

    if (!referralData.SOARContactId) {
      createContactRecords(state);
    } else {
      updateContactRecord(state);
    }
  };

  const handleDefer = async () => {
    if (
      dynamicData.deferralReasons &&
      dynamicData.deferralReasons.value !== undefined &&
      dynamicData.deferralReasons.value !== ""
    ) {
      // const obj = { ...referralData };
      // obj.Status = "Deferred";
      // setReferralData(obj);

      // const selectedGender = contactOptionSets.gendercode.filter(
      //   (option) => option._id === referralData.GenderValue
      // )[0];
      // const referral = {
      //   CreatedById: safyInfo.user.azureId,
      //   DOB: referralData.DOB || "",
      //   DateofReferral: referralData.DateofReferral,
      //   DeferralReason: dynamicData.deferralReasons.value || "",
      //   DynamicFields: convertDynamicData(),
      //   FirstName: referralData.FirstName,
      //   GenderText: (selectedGender && selectedGender.name) || "",
      //   GenderValue: referralData.GenderValue || "",
      //   LastName: referralData.LastName,
      //   MiddleName: referralData.MiddleName || "",
      //   ModifiedById: safyInfo.user.azureId,
      //   ProposedProgram: referralData.ProposedProgram,
      //   ProposedProgramName: referralData.ProposedProgramName,
      //   ReferralId: referralData.ReferralId,
      //   ReferringAgency:
      //     referralData.ReferringAgency === undefined ||
      //     referralData.ReferringAgency === ""
      //       ? ""
      //       : referralData.ReferringAgency,
      //   SSN: referralData.SSN || "",
      //   Status: "Deferred",
      //   StatusChangeDate: new Date(),
      //   SOARContactId: "",
      //   SOARClientProgramId: "",
      //   SOARClientProgramName: "",
      // };

      // saveReferral(referral, org, "Update").then((response) => {
      //   if (response) {
      //     const result = JSON.parse(response);
      //     if (result.SystemResponse[0].Code === "SUCCESS") {
      //       toast.success("Referral Successfully Deferred");
      //       if (updateReferrals) updateReferrals();
      //     } else {
      //       toast.error("A problem occurred when deferring the Referral");
      //     }
      //   }
      // });

      await defer(dynamicData.deferralReasons.value);

      hideModal();
    }
  };

  const handleTransfer = async () => {
    const obj = { ...dynamicData };
    obj.deferralReasons.value = "Transfered";
    obj.deferralReasons.text = "Transfered";
    setDynamicData(obj);
    await defer("Transfered");
    await clone();
    hideModal();
  };

  const defer = (defReason) => {
    const obj = { ...referralData };
    obj.Status = "Deferred";
    setReferralData(obj);

    const selectedGender = contactOptionSets.gendercode.filter(
      (option) => option._id === referralData.GenderValue
    )[0];

    const referral = {
      CreatedById: referralData.CreatedById,
      DOB: referralData.DOB
        ? referralData.DOB["@date"]
          ? referralData.DOB["@date"]
          : referralData.DOB
        : "",
      DateofReferral: referralData.DateofReferral,
      DeferralReason: defReason,
      DynamicFields: convertDynamicData(),
      FirstName: referralData.FirstName,
      GenderText: (selectedGender && selectedGender.name) || "",
      GenderValue: referralData.GenderValue || "",
      LastName: referralData.LastName,
      MiddleName: referralData.MiddleName || "",
      ModifiedById: safyInfo.user.azureId,
      ProposedProgram: referralData.ProposedProgram,
      ProposedProgramName: referralData.ProposedProgramName,
      ReferralId: referralData.ReferralId,
      ReferringAgencyId:
        referralData.ReferringAgencyId === undefined ||
        referralData.ReferringAgencyId === ""
          ? ""
          : referralData.ReferringAgencyId,
      ReferringAgencyName:
        referralData.ReferringAgencyName === undefined ||
        referralData.ReferringAgencyName === ""
          ? ""
          : referralData.ReferringAgencyName,
      SSN: referralData.SSN || "",
      Status: "Deferred",
      StatusChangeDate:
        dynamicData.DeferralDate && dynamicData.DeferralDate.value
          ? dynamicData.DeferralDate.value
          : new Date(),
      SOARContactId: referralData.SOARContactId || "",
      SOARClientProgramId: "",
      SOARClientProgramName: "",
    };

    console.log("Defer Object", referral);

    saveReferral(referral, org, "Update")
      .then((response) => {
        if (response) {
          const result = JSON.parse(response);
          if (result.SystemResponse[0].Code === "SUCCESS") {
            toast.success("Referral Successfully Deferred");
            if (updateReferrals) updateReferrals();
          } else {
            toast.error("A problem occurred when deferring the Referral");
            writeLog(referral, "Update", response);
          }
        }
      })
      .catch((error) => {
        toast.error("A problem occurred when deferring the Referral");
        writeLog(referral, "Update", error);
      });
  };

  const clone = () => {
    const selectedGender = contactOptionSets.gendercode.filter(
      (option) => option._id === referralData.GenderValue
    )[0];
    const referral = {
      CreatedById: safyInfo.user.azureId,
      DOB: referralData.DOB
        ? referralData.DOB["@date"]
          ? referralData.DOB["@date"]
          : referralData.DOB
        : "",
      DateofReferral: new Date(),
      DeferralReason: "",
      DynamicFields: convertProgramData(transferProgram),
      FirstName: referralData.FirstName,
      GenderText: (selectedGender && selectedGender.name) || "",
      GenderValue: referralData.GenderValue || "",
      LastName: referralData.LastName,
      MiddleName: referralData.MiddleName || "",
      ModifiedById: safyInfo.user.azureId,
      ProposedProgram: transferProgram,
      ProposedProgramName: programUnitOptions.filter(
        (p) => p._id === transferProgram
      )[0].name,
      ReferralId: "",
      ReferringAgencyId:
        referralData.ReferringAgencyId === undefined ||
        referralData.ReferringAgencyId === ""
          ? ""
          : referralData.ReferringAgencyId,
      ReferringAgencyName:
        referralData.ReferringAgencyName === undefined ||
        referralData.ReferringAgencyName === ""
          ? ""
          : referralData.ReferringAgencyName,
      SSN: referralData.SSN || "",
      Status: "Pending",
      StatusChangeDate: new Date(),
      SOARContactId: referralData.SOARContactId || "",
      SOARClientProgramId: "",
      SOARClientProgramName: "",
    };

    console.log("Transfer Object", referral);

    saveReferral(referral, org, "Insert")
      .then((response) => {
        if (response) {
          const result = JSON.parse(response);
          if (result.SystemResponse[0].Code === "SUCCESS") {
            toast.success("Transfer successfully created");
            navigate(`/referral/${result.SystemResponse[0].Id}`);
          } else {
            toast.error("A problem occurred when transferring programs");
            writeLog(referral, "Insert", response);
          }
        }
      })
      .catch((error) => {
        toast.error("A problem occurred when transferring programs");
        writeLog(referral, "Insert", error);
      });
  };

  const writeLog = (bodyObj, Action, response) => {
    const logData = {
      Who: `${safyInfo.user.domainName}|${safyInfo.user.displayName}`,
      API: "Eureka API",
      APIBody: JSON.stringify(bodyObj),
      URL: `https://eurekaapi.azurewebsites.net/Component/Upsert/Referral/${org}/${Action}/`,
      Org: logOrg,
      Application: `Referral Decision Modal - ${Action}`,
      FullError: JSON.stringify({
        response,
      }),
      ResponseCode: response.Status || "Unknown",
      ResponseMessage: response.Message || "Unknown",
      ExceptionType: response.ClassName || "Unknown",
    };
    writeErrorLog(logData);
  };

  const createAdmissionRecords = (
    contactId,
    clientCompanyId,
    state,
    toastId
  ) => {
    const clientProgramObj = {
      ccx_patient: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: contactId,
        },
      },
      ccx_company: {
        type: "entity",
        value: {
          entityName: "ccx_clientagency",
          entityId: clientCompanyId,
        },
      },
      ccx_programunit: {
        type: "entity",
        value: {
          entityName: "ccx_unit",
          entityId: referralData.ProposedProgram,
        },
      },
      ccx_admitdate: {
        type: "datetime",
        value: new Date(dateOfAdmission),
      },
    };

    createEntity(
      "ccx_client_program",
      JSON.stringify(clientProgramObj),
      safyInfo,
      state
    ).then((response) => {
      const clientProgramResult = response.resultset.result;

      if (referralData.DOB)
        clientProgramResult["con.birthdate"] = {
          "#text": `${referralData.DOB}T10:59:00`,
          "@date": moment(referralData.DOB).format("L"),
          "@time": moment.utc(`${referralData.DOB}T10:59:00`).format("LT"),
        };
      setClientProgram(clientProgramResult);

      const selectedGender = contactOptionSets.gendercode.filter(
        (option) => option._id === referralData.GenderValue
      )[0];

      const referral = {
        CreatedById: referralData.CreatedById,
        DOB: referralData.DOB
          ? referralData.DOB["@date"]
            ? referralData.DOB["@date"]
            : referralData.DOB
          : "",
        DateofReferral: referralData.DateofReferral,
        DeferralReason: referralData.DeferralReason || "",
        DynamicFields: convertDynamicData(),
        FirstName: referralData.FirstName,
        GenderText: (selectedGender && selectedGender.name) || "",
        GenderValue: referralData.GenderValue || "",
        LastName: referralData.LastName,
        MiddleName: referralData.MiddleName || "",
        ModifiedById: safyInfo.user.azureId,
        ProposedProgram: referralData.ProposedProgram,
        ProposedProgramName: referralData.ProposedProgramName,
        ReferralId: referralData.ReferralId,
        ReferringAgencyId:
          referralData.ReferringAgencyId === undefined ||
          referralData.ReferringAgencyId === ""
            ? ""
            : referralData.ReferringAgencyId,
        ReferringAgencyName:
          referralData.ReferringAgencyName === undefined ||
          referralData.ReferringAgencyName === ""
            ? ""
            : referralData.ReferringAgencyName,
        SSN: referralData.SSN || "",
        Status: "Accepted",
        StatusChangeDate: new Date(),
        SOARContactId: referralData.SOARContactId || "",
        SOARClientProgramId: clientProgramResult.ccx_client_programid || "",
        SOARClientProgramName: referralData.SOARClientProgramName || "",
      };

      saveReferral(referral, org, "Update")
        .then((r2) => {
          if (response) {
            const result = JSON.parse(r2);
            if (result.SystemResponse[0].Code === "SUCCESS") {
              toast.success("Referral Successfully Accepted");
              if (updateReferrals) updateReferrals();
            } else {
              toast.error(
                "A problem occurred when accepting the Referral - No Success Code"
              );
              writeLog(referral, "Update", response);
            }
          }
        })
        .catch((error) => {
          toast.error(
            "A problem occurred when accepting the Referral - Error Catch"
          );
          writeLog(referral, "Update", error);
        });

      if (getProgramUnitType() !== "TFC") {
        setIsUpdateRecordsModalLoading(false);

        toast.update(toastId, {
          render: "Admission Records created.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
      }
    });

    if (getProgramUnitType() === "TFC") {
      const fosterHomeProgramUnit = getFosterHomeProgramUnit();

      if (fosterHomeProgramUnit) {
        const fosterHomeProgramObj = {
          ccx_patient: {
            type: "entity",
            value: {
              entityName: "contact",
              entityId: contactId,
            },
          },
          ccx_company: {
            type: "entity",
            value: {
              entityName: "ccx_clientagency",
              entityId: clientCompanyId,
            },
          },
          ccx_programunit: {
            type: "entity",
            value: {
              entityName: "ccx_unit",
              entityId: fosterHomeProgramUnit.ccx_unitid,
            },
          },
          ccx_admitdate: {
            type: "datetime",
            value: new Date(dateOfAdmission),
          },
        };

        createEntity(
          "ccx_client_program",
          JSON.stringify(fosterHomeProgramObj),
          safyInfo,
          state
        ).then((fosterHomeProgramResponse) => {
          const fosterHomeProgramResult =
            fosterHomeProgramResponse.resultset.result;
          setFosterHomeProgram(fosterHomeProgramResult);
          setIsUpdateRecordsModalLoading(false);

          getClientDiagnosisDetails(contactId).then((response) => {
            if (response) {
              const bedDiagnosis = response.filter(
                (dxDetails) =>
                  dxDetails.ccx_expdate === undefined &&
                  dxDetails["dx.ccx_expdate"] === undefined &&
                  getFieldName(dxDetails.ccx_clientdiagnosis) ===
                    "Bed Diagnosis"
              )[0];
              if (!bedDiagnosis) {
                createBedDiagnosisDetail(
                  contactId,
                  fosterHomeProgramResult,
                  state,
                  toastId
                );
              } else {
                const diagnosisDetailObj = {
                  ccx_contact: {
                    type: "entity",
                    value: {
                      entityName: "contact",
                      entityId: contactId,
                    },
                  },
                  ccx_clientdiagnosis: {
                    type: "entity",
                    value: {
                      entityName: "ccx_clientdiagnosis",
                      entityId: getFieldText(bedDiagnosis.ccx_clientdiagnosis),
                    },
                  },
                  ccx_clientprogram: {
                    type: "entity",
                    value: {
                      entityName: "ccx_client_program",
                      entityId: fosterHomeProgramResult.ccx_client_programid,
                    },
                  },
                  ccx_rank: {
                    type: "number",
                    value: 1,
                  },
                  ccx_effdate: {
                    type: "datetime",
                    value: new Date(dateOfAdmission),
                  },
                };

                createEntity(
                  "ccx_clientdiagnosisdetail",
                  JSON.stringify(diagnosisDetailObj),
                  safyInfo,
                  state
                ).then(() => {
                  toast.update(toastId, {
                    render: "Admission Records created.",
                    type: toast.TYPE.SUCCESS,
                    isLoading: false,
                    autoClose: 5000,
                    closeOnClick: true,
                  });
                });
              }
            } else {
              createBedDiagnosisDetail(
                contactId,
                fosterHomeProgramResult,
                state,
                toastId
              );
            }
          });
        });
      }
    }
  };

  const createBedDiagnosisDetail = (
    contactId,
    fosterHomeProgramResult,
    state,
    toastId
  ) => {
    const diagnosisObj = {
      ccx_contactid: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: contactId,
        },
      },
      ccx_name: {
        type: "string",
        value: "Bed Diagnosis",
      },
      ccx_diagnosistype: {
        type: "optionset",
        value: 803080002,
      },
      ccx_icd10: {
        type: "entity",
        value: {
          entityName: "ccx_icd10",
          entityId: "{2CFB755B-BED6-E711-A88A-005056822045}", // Bed Diagnosis
        },
      },
      ccx_code: {
        type: "string",
        value: "BD",
      },
      ccx_sourceofdata: {
        type: "optionset",
        value: 803080000,
      },
      ccx_diagnosingprovider: {
        type: "entity",
        value: {
          entityName: "systemuser",
          entityId: "{8B53EFBE-DC1E-E711-B4D4-00155D1E1FBD}", // Admin, crmadmin
        },
      },
      ccx_effdate: {
        type: "datetime",
        value: new Date(dateOfAdmission),
      },
    };

    createEntity(
      "ccx_clientdiagnosis",
      JSON.stringify(diagnosisObj),
      safyInfo,
      state
    ).then((diagnosisResponse) => {
      const diagnosisResult = diagnosisResponse.resultset.result;

      const diagnosisDetailObj = {
        ccx_contact: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: contactId,
          },
        },
        ccx_clientdiagnosis: {
          type: "entity",
          value: {
            entityName: "ccx_clientdiagnosis",
            entityId: diagnosisResult.ccx_clientdiagnosisid,
          },
        },
        ccx_clientprogram: {
          type: "entity",
          value: {
            entityName: "ccx_client_program",
            entityId: fosterHomeProgramResult.ccx_client_programid,
          },
        },
        ccx_rank: {
          type: "number",
          value: 1,
        },
        ccx_effdate: {
          type: "datetime",
          value: new Date(dateOfAdmission),
        },
      };

      createEntity(
        "ccx_clientdiagnosisdetail",
        JSON.stringify(diagnosisDetailObj),
        safyInfo,
        state
      ).then(() => {
        toast.update(toastId, {
          render: "Admission Records created.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        setIsUpdateRecordsModalOpen(true);
      });
    });
  };

  function convertDynamicData() {
    let dynamicObj = (
      "<results>" +
      json2xml(dynamicData, { compact: true, igonoreComment: true }) +
      "</results>"
    ).replaceAll("\n", "&#10;");

    return dynamicObj;
  }

  function convertProgramData(id) {
    const config =
      programUnitOptions.filter((p) => p._id === id)[0].referralConfig || {};
    let d = { ...dynamicData };
    delete d.DeferralDate;
    if (config.deferralReasons) d.deferralReasons = config.deferralReasons;
    if (config.ReferralConfig) d.ReferralConfig = config.ReferralConfig;

    let dynamicObj = (
      "<results>" +
      json2xml(d, { compact: true, igonoreComment: true }) +
      "</results>"
    ).replaceAll("\n", "&#10;");

    return dynamicObj;
  }

  const handleDynamicDataChange = ({ currentTarget: input }) => {
    const data = { ...dynamicData };
    if (!data[input.name]) {
      data[input.name] = { value: input.value, text: input.value.toString() };
    }
    data[input.name].value =
      input.type === "checkbox" ? input.checked.toString() : input.value;
    data[input.name].text =
      input.type === "checkbox"
        ? input.checked.toString()
        : input.value.toString();
    setDynamicData(data);
  };

  const getProgramUnitCompany = () => {
    const programUnit = programUnits.filter(
      (unit) => unit.ccx_unitid === `{${referralData.ProposedProgram}}`
    )[0];
    if (programUnit)
      return {
        companyId: getFieldText(programUnit["facility.ccx_agency"]),
        companyName: getFieldName(programUnit["facility.ccx_agency"]),
      };
  };

  const getProgramUnitType = () => {
    const programUnit = programUnits.filter(
      (unit) => unit.ccx_unitid === `{${referralData.ProposedProgram}}`
    )[0];

    if (programUnit) return getFieldName(programUnit.ccx_programtype);
  };

  const getFosterHomeProgramUnit = () => {
    const tfcUnit = programUnits.filter(
      (unit) => unit.ccx_unitid === `{${referralData.ProposedProgram}}`
    )[0];
    const fosterHome = programUnits.filter(
      (unit) =>
        getFieldText(unit.statecode) === "0" &&
        getFieldText(unit.ccx_facility) ===
          getFieldText(tfcUnit.ccx_facility) &&
        getFieldName(unit.ccx_programtype) === "Foster Home"
    )[0];
    return fosterHome;
  };

  const handleUpdateAddresses = (doUpdate = true) => {
    setUpdateAddresses(doUpdate);
  };

  useEffect(() => {
    if (clientId || updateAddresses) {
      console.log("Getting Addresses for ", clientId);
      getAddresses(clientId).then((response) => {
        if (response && isMountedRef.current) {
          console.log("Addresses:", response);
          setAddresses(response);
          // addToTimeline(
          //   response,
          //   "Address",
          //   "ccx_contactaddressid",
          //   "ccx_name",
          //   "ccx_effectivedate",
          //   "ccx_expirationdate",
          //   "createdby",
          //   "createdon"
          // );
        }
      });
      setUpdateAddresses(false);
    } else {
      setAddresses([]);
    }
  }, [clientId, updateAddresses]);

  return (
    <>
      {isUpdateRecordsModalOpen ? (
        <UpdateRecordsModal
          forceOpen={isUpdateRecordsModalOpen}
          forceClose={() => {
            setIsUpdateRecordsModalOpen(false);
          }}
          clientId={clientId}
          clientProgram={clientProgram}
          fosterHomePrograms={fosterHomeProgram ? [fosterHomeProgram] : []}
          isLoading={isUpdateRecordsModalLoading}
          addresses={addresses}
          updateAddresses={handleUpdateAddresses}
        ></UpdateRecordsModal>
      ) : null}
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Referral</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {referralStatus === "Accepted" && (
              <>
                <p>
                  Records would be created for{" "}
                  <b>{referralData.ProposedProgramName}</b>
                </p>
                <Input
                  name="DateOfAdmission"
                  label="Date of Admission"
                  type="date"
                  onChange={(e) => {
                    if (isDateOfAdmissionValid(e.currentTarget.value))
                      setDateOfAdmission(e.currentTarget.value);
                  }}
                  error={errors.DateOfAdmission}
                />
              </>
            )}
            {referralStatus === "Deferred" &&
              (dynamicData !== undefined &&
              dynamicData.deferralReasons !== undefined &&
              dynamicData.deferralReasons.options !== undefined ? (
                <>
                  <Input
                    name="DeferralDate"
                    label="Date of Deferral"
                    type="date"
                    onChange={handleDynamicDataChange}
                    value={
                      dynamicData &&
                      dynamicData.DeferralDate &&
                      dynamicData.DeferralDate.value
                        ? dynamicData.DeferralDate.value
                        : ""
                    }
                    error={errors.DeferralDate}
                  />
                  <Select
                    name="deferralReasons"
                    options={dynamicData.deferralReasons.options}
                    label="Deferral Reason"
                    value={dynamicData.deferralReasons.value || ""}
                    onChange={handleDynamicDataChange}
                    key="seldeferralReason"
                  />
                </>
              ) : (
                <>
                  <Input
                    name="DeferralDate"
                    label="Date of Deferral"
                    type="date"
                    onChange={handleDynamicDataChange}
                    value={
                      dynamicData !== undefined &&
                      dynamicData.DeferralDate !== undefined
                        ? dynamicData.DeferralDate.value
                        : ""
                    }
                    error={errors.DeferralDate}
                  />
                  <Input
                    name="deferralReasons"
                    label="Deferral Reason"
                    value={
                      dynamicData !== undefined &&
                      dynamicData.deferralReasons !== undefined
                        ? dynamicData.deferralReasons.value
                        : ""
                    }
                    onChange={handleDynamicDataChange}
                  />
                </>
              ))}
            {referralStatus === "Transfer" && (
              <>
                <div className="mb-4">
                  This referral will be deferred with a status of "Transfer" and
                  a new Referral will be created for the chosen Program Unit.
                  Any completed data if applicable will be transferred to the
                  new Referral.
                </div>
                <Input
                  name="DeferralDate"
                  label="Date of Transfer"
                  type="date"
                  onChange={handleDynamicDataChange}
                  value={
                    dynamicData !== undefined &&
                    dynamicData.DeferralDate !== undefined
                      ? dynamicData.DeferralDate.value
                      : ""
                  }
                  error={errors.DeferralDate}
                />
                <Select
                  name="programUnit"
                  options={programUnitOptions}
                  label="Program to Transfer to:"
                  value={transferProgram}
                  onChange={(e) => {
                    setTransferProgram(e.currentTarget.value);
                  }}
                  key="transferProgram"
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <>
              <button className="btn btn-danger" onClick={hideModal}>
                Cancel
              </button>
              <button
                className={
                  Object.keys(errors).length === 0
                    ? "btn btn-primary"
                    : "btn btn-secondary"
                }
                disabled={
                  referralStatus === "Deferred" &&
                  dynamicData.deferralReasons &&
                  (dynamicData.deferralReasons.value === undefined ||
                    dynamicData.deferralReasons.value === "" ||
                    dynamicData.deferralReasons.value === null)
                }
                onClick={() => {
                  if (referralStatus === "Accepted") handleAccept();
                  if (referralStatus === "Deferred") handleDefer();
                  if (referralStatus === "Transfer") handleTransfer();
                }}
              >
                Save
              </button>
            </>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default ReferralDecisionModal;
